import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  api,
  type EditionRow,
  type LicenceRow,
  type ReviewQueue,
  type UserRow,
  type UtilisationRow,
} from '../lib/api'
import { Empty } from '../components/Page'
import { Card, CardBar, ConsoleHead, StatCell } from '../components/admin/Console'
import { useSession } from '../lib/session'

/**
 * The console's first screen.
 *
 * It answers the three questions an operator arrives with: who may read what,
 * how many may read it at once, and what has been ingested. Every figure is
 * counted from the API rather than asserted, and a figure that cannot be
 * counted is absent rather than guessed.
 */
export function OverviewPage() {
  const { me, can } = useSession()

  const licences = useQuery({
    queryKey: ['admin', 'licences'],
    queryFn: () => api.get<LicenceRow[]>('/admin/licences'),
    enabled: can('admin.licences.manage'),
  })

  const editions = useQuery({
    queryKey: ['admin', 'editions'],
    queryFn: () => api.get<EditionRow[]>('/admin/ingest/editions?limit=100'),
    enabled: can('admin.ingest.manage'),
  })

  const users = useQuery({
    queryKey: ['admin', 'users', ''],
    queryFn: () => api.get<UserRow[]>('/admin/users'),
    enabled: can('institution.users.manage') || can('admin.users.manage'),
  })

  const queue = useQuery({
    queryKey: ['review', 'queue', 'overview'],
    queryFn: () => api.get<ReviewQueue>('/review/queue?status=open&limit=1'),
    enabled: can('review.queue.view'),
    retry: false,
  })

  const pages = (editions.data ?? []).reduce((sum, row) => sum + (row.page_count ?? 0), 0)

  return (
    <>
      <ConsoleHead title="Overview" note={me?.institution?.name ?? undefined} />

      <Card className="mb-6 max-w-[1000px]">
        <div className="grid grid-cols-2 md:grid-cols-5">
          <StatCell label="Editions" value={(editions.data?.length ?? 0).toLocaleString()} />
          <StatCell label="Pages" value={pages.toLocaleString()} />
          <StatCell label="Licences" value={(licences.data?.length ?? 0).toLocaleString()} />
          <StatCell label="Accounts" value={(users.data?.length ?? 0).toLocaleString()} />
          <StatCell
            label="In review"
            value={queue.isError ? 'no queue API' : (queue.data?.total ?? 0).toLocaleString()}
            tone={queue.data?.total ? 'mark' : undefined}
          />
        </div>
      </Card>

      <div className="grid max-w-[1000px] items-start gap-6 lg:grid-cols-2">
        <Card>
          <CardBar>
            <h2 className="chrome text-[11px]">Licences</h2>
            <Link to="/admin/seats" className="ml-auto text-[13.5px] text-accent">
              Live sessions and queue
            </Link>
          </CardBar>
          {!can('admin.licences.manage') ? (
            <Empty>You do not manage licences.</Empty>
          ) : licences.isLoading ? (
            <Empty>Reading the licences.</Empty>
          ) : !licences.data?.length ? (
            <Empty>
              No licence exists yet. A publication becomes readable only when an institution holds a
              licence over it, so this is the first thing to configure.
            </Empty>
          ) : (
            licences.data.map((licence, index) => (
              <div
                key={licence.id}
                className="px-4 py-3"
                style={{
                  borderTop: index ? '1px solid var(--color-neutral-200)' : 'none',
                }}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[14.5px] font-semibold">
                    {licence.publication_name} · {licence.institution_name}
                  </span>
                  <SeatSummary licence={licence} />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.04em] text-ink2">
                  {licence.access_model}
                  {licence.access_model === 'concurrent' && ` · limit ${licence.concurrent_limit}`}
                  {licence.is_trial && ' · trial'}
                  {licence.starts_on && ` · from ${licence.starts_on}`}
                </div>
              </div>
            ))
          )}
        </Card>

        <Card>
          <CardBar>
            <h2 className="chrome text-[11px]">Recent editions</h2>
            <Link to="/admin/ingest" className="ml-auto text-[13.5px] text-accent">
              Ingest control
            </Link>
          </CardBar>
          {!can('admin.ingest.manage') ? (
            <Empty>You do not manage ingest.</Empty>
          ) : editions.isLoading ? (
            <Empty>Reading the editions.</Empty>
          ) : !editions.data?.length ? (
            <Empty>
              Nothing ingested yet. Drop a PDF into the worker inbox and register it with{' '}
              <code className="font-mono text-[13px]">make ingest FILE=… PUB=…</code>
            </Empty>
          ) : (
            editions.data.slice(0, 5).map((edition, index) => (
              <Link
                key={edition.id}
                to={`/admin/ingest/${edition.id}`}
                className="block px-4 py-3 text-body no-underline hover:bg-surface"
                style={{ borderTop: index ? '1px solid var(--color-neutral-200)' : 'none' }}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[14.5px] font-semibold">
                    {edition.pub_date ?? 'undated'} · {edition.source_filename}
                  </span>
                  <span className="font-mono text-[10px] text-ink2">{edition.ingest_status}</span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.04em] text-ink2">
                  route {edition.route ?? 'undecided'} · {edition.page_count ?? '-'} pp ·{' '}
                  {edition.page_w_pt ?? '-'}×{edition.page_h_pt ?? '-'} pt
                </div>
              </Link>
            ))
          )}
        </Card>

        <Card className="lg:col-span-2">
          <CardBar>
            <h2 className="chrome text-[11px]">Your access</h2>
          </CardBar>
          <div className="px-4 py-3 text-[14.5px] leading-normal">
            <p className="text-ink2">
              Signed in as {me?.display_name} ({me?.email}).
            </p>
            <p className="mt-1.5">
              Roles: <span className="font-mono text-[13px]">{me?.roles.join(', ') || 'none'}</span>
            </p>
            <p className="mt-1 text-ink2">
              {me?.permissions.length} permissions resolved from those roles. Permissions are
              database-driven; a role can be recomposed without a deploy, and nothing in the
              application tests a role by name.
            </p>
          </div>
        </Card>
      </div>
    </>
  )
}

function SeatSummary({ licence }: { licence: LicenceRow }) {
  const { can } = useSession()
  const utilisation = useQuery({
    queryKey: ['admin', 'utilisation', licence.id],
    queryFn: () => api.get<UtilisationRow>(`/admin/licences/${licence.id}/utilisation`),
    enabled: can('admin.sessions.view'),
    refetchInterval: 15_000,
  })

  if (!utilisation.data) return null
  const { seats_in_use, seat_limit, queue_depth } = utilisation.data
  return (
    <span className="font-mono text-[11px] text-ink2">
      {seats_in_use}
      {seat_limit ? ` of ${seat_limit}` : ''} in use
      {queue_depth > 0 ? ` · ${queue_depth} waiting` : ''}
    </span>
  )
}
